import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * TrackingScripts Component
 * Dynamically injects Google Analytics and Facebook Pixel scripts based on CMS settings
 * Tracks page views on route changes
 */
export const TrackingScripts = () => {
  const location = useLocation();

  useEffect(() => {
    // Fetch site settings and inject tracking scripts
    const initializeTracking = async () => {
      try {
        const response = await axios.get(`${API}/site-settings/public`);
        const { google_analytics_id, facebook_pixel_id } = response.data;

        // Inject Google Analytics (GA4)
        if (google_analytics_id && !window.gtag) {
          injectGoogleAnalytics(google_analytics_id);
        }

        // Inject Facebook Pixel
        if (facebook_pixel_id && !window.fbq) {
          injectFacebookPixel(facebook_pixel_id);
        }
      } catch (error) {
        console.error('Failed to load tracking settings:', error);
      }
    };

    initializeTracking();
  }, []); // Run once on mount

  // Track page view on route change
  useEffect(() => {
    // Google Analytics page view
    if (window.gtag) {
      window.gtag('config', window.GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }

    // Facebook Pixel page view
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  return null; // This component doesn't render anything
};

/**
 * Inject Google Analytics (GA4) script
 */
const injectGoogleAnalytics = (measurementId) => {
  // Store measurement ID globally for page tracking
  window.GA_MEASUREMENT_ID = measurementId;

  // Create and inject gtag script
  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(gtagScript);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false, // We'll handle page views manually in SPA
  });

  console.log('✅ Google Analytics initialized:', measurementId);
};

/**
 * Inject Facebook Pixel script
 */
const injectFacebookPixel = (pixelId) => {
  // Initialize Facebook Pixel
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  console.log('✅ Facebook Pixel initialized:', pixelId);
};

/**
 * Helper function to track lead conversion events
 * Call this from ServiceLeadForm after successful submission
 */
export const trackLeadConversion = (leadData) => {
  const { service, pole, city, source, campaign } = leadData;

  // Google Analytics conversion event
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      event_category: 'Lead',
      event_label: `${service} - ${pole || 'N/A'}`,
      service: service,
      pole: pole,
      city: city,
      source: source,
      campaign: campaign,
    });
    console.log('📊 GA Event: generate_lead', { service, pole, city });
  }

  // Facebook Pixel lead event
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: `${service} - ${pole || 'N/A'}`,
      content_category: service,
      value: 0, // Can be updated if you have a lead value
      currency: 'CHF',
      // Custom parameters
      service: service,
      pole: pole,
      city: city,
      source: source,
      campaign: campaign,
    });
    console.log('📊 FB Pixel Event: Lead', { service, pole, city });
  }
};
